import { useEffect, useRef } from 'react';
import type Lenis from 'lenis';

/**
 * @param disabled Skip initializing Lenis entirely. Lenis takes over the
 * page's native scroll globally (no `wrapper` option is passed, so it binds
 * to the document) and runs its own requestAnimationFrame loop. That's fine
 * for exactly one instance on a real invitation page — it breaks completely
 * if more than one mounts at once (e.g. several SceneExperienceRenderer
 * instances inside a template-picker grid): competing instances fight over
 * the same scroll target and the page's native scroll can end up stuck.
 * Callers rendering multiple simultaneous instances must pass `true`.
 */
export function useLenisScroll(disabled = false): React.RefObject<Lenis | null> {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    if (disabled) return;

    // Respect prefers-reduced-motion: never initialize smooth scroll for users
    // who have requested reduced motion (accessibility requirement).
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    let lenis: Lenis;

    import('lenis').then(({ default: LenisClass }) => {
      lenis = new LenisClass({ autoRaf: true });
      lenisRef.current = lenis;
    });

    return () => {
      lenis?.destroy();
      lenisRef.current = null;
    };
  }, [disabled]);

  return lenisRef;
}
